import RootState from '@/app/appStore';
import { updateRows } from '../row/slice';
import { makeSelectFilteredRowsByTableId, makeSelectNonGroupColumns, makeSelectRowById, makeSelectTableColumnsByTableId, makeSelectTableStateByTableId, selectFlattenedColumns } from "../selectors";
import { BaseColumn, CellDimensions, Column, SelectedColumn, SummaryColumn, TableState, ValueType } from "../types";
import { MIN_SCROLLBAR_HANDLE_SIZE, ROW_HEIGHT, SCROLLBAR_WIDTH, THRESHOLD } from "./constants";
import { adjustScroll } from './logic';
import { addCheckedRowData, removeCheckedRowData, setScroll, setSelectedCell } from './slice';

/**
 * Summary 영역 확인 함수
 */
export const isInSummaryArea = (tableState: any, y: number, tableHeight: number): boolean => {
    if (!tableState.hasSummaryColumn) return false;

    const maxDepth = calculateSummaryColumnMaxDepth(Object.values(tableState.summaryColumns)) + 1;
    const summaryHeight = maxDepth * ROW_HEIGHT;
    const summaryY = tableHeight - SCROLLBAR_WIDTH - summaryHeight;

    return y >= summaryY;
};

/**
 * 컬럼 너비 가져오기 함수 생성
 */
export const createColumnWidthGetter = (columns: { [key: string]: any }) => (columnId: string): number => {
    return columns[columnId]?.width || 0;
};

/**
 * 특정 셀의 화면상 위치와 크기를 계산하는 함수
 * 
 * @param canvasRef - Canvas 요소에 대한 참조
 * @param tableId - 테이블의 고유 식별자
 * @param rowIndex - 계산할 셀의 행 인덱스
 * @param columnIndex - 계산할 셀의 열 인덱스
 * @returns CellDimensions | null - 셀의 위치와 크기 정보 또는 계산 불가능한 경우 null
 * 
 * @description
 * 1. 셀의 위치가 유효한지 검증
 * 2. 현재 스크롤 위치를 고려하여 셀이 화면에 보이는지 확인
 * 3. 보이는 셀에 대해서만 위치와 크기를 계산
 * 4. 계산된 값은 캔버스 상의 실제 픽셀 좌표
 */
export const getCellDimensions = (
    canvasRef: React.RefObject<HTMLCanvasElement>,
    tableId: string,
    rowIndex: number,
    columnIndex: number,
): CellDimensions | null => {
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    const rows = makeSelectFilteredRowsByTableId(tableId)(RootState.getState());
    const columns = makeSelectNonGroupColumns(tableId)(RootState.getState()).filter(column => column.visible !== false);

    if (
        rowIndex < 0 ||
        rowIndex >= rows.length ||
        columnIndex < 0 ||
        columnIndex >= columns.length
    ) {
        return null;
    }

    const canvas = canvasRef.current;
    if (!canvas) return null;

    const HEADER_HEIGHT = calculateHeaderHeight(tableState.columns);
    const tableHeight = canvas.clientHeight || 0;

    // 보이는 행 계산
    const startRowIndex = Math.floor(tableState.scrollY / ROW_HEIGHT);
    const visibleRowCount = Math.ceil((tableHeight - HEADER_HEIGHT) / ROW_HEIGHT);
    const relativeRowIndex = rowIndex - startRowIndex;

    if (relativeRowIndex < 0 || relativeRowIndex >= visibleRowCount) {
        return null; // 행이 보이지 않는 경우
    }

    // 셀의 Y 위치 계산
    const y = relativeRowIndex * ROW_HEIGHT + HEADER_HEIGHT;
    const columnWidths = columns.map(col => col.width);

    // 셀의 X 위치 계산
    const x = columnWidths.slice(0, columnIndex).reduce((acc, width) => acc + width, 0) - tableState.scrollX;

    // 셀의 너비와 높이 계산
    const width = (columnWidths[columnIndex] || 0) + 1;
    const height = ROW_HEIGHT + 1;

    return { x, y, width, height };
};

/**
 * 마우스 클릭 위치에 해당하는 셀의 행과 열 인덱스를 계산하는 함수
 * 
 * @param x - 마우스의 X 좌표
 * @param y - 마우스의 Y 좌표
 * @param tableId - 테이블의 고유 식별자
 * @param tableHeight - 테이블의 전체 높이
 * @returns {{ rowIndex: number, columnIndex: number } | null} - 셀의 위치 정보 또는 유효하지 않은 위치인 경우 null
 */
export const getCellPosition = (
    x: number,
    y: number,
    tableId: string,
    tableHeight: number
): { rowIndex: number, columnIndex: number } | null => {
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    const rows = makeSelectFilteredRowsByTableId(tableId)(RootState.getState());

    // summaryColumn 영역 확인 로직 추가
    if (isInSummaryArea(tableState, y, tableHeight)) {
        return null;
    }

    /**
     * 중첩된 그룹 컬럼 구조를 평탄화하여 일반 컬럼 배열로 변환하는 함수
     * 
     * @param columns - 처리할 컬럼 배열
     * @returns Column[] - 평탄화된 컬럼 배열
     */
    // GROUP이 아닌 모든 컬럼들을 순서대로 가져옴
    const nonGroupColumns = makeSelectNonGroupColumns(tableId)(RootState.getState()).filter(column => column.visible !== false);

    const HEADER_HEIGHT = calculateHeaderHeight(tableState.columns);
    const ROW_HEIGHT_PIXELS = ROW_HEIGHT;

    // 헤더 영역 클릭 시 null 반환 (헤더는 별도로 처리)
    if (y < HEADER_HEIGHT) return null;

    // 스크롤 위치를 고려하여 실제 행 인덱스 계산
    const startRowIndex = Math.floor(tableState.scrollY / ROW_HEIGHT_PIXELS);
    const visibleRowCount = Math.ceil((tableHeight - HEADER_HEIGHT) / ROW_HEIGHT_PIXELS);
    // 클릭 위치의 상대적 행 인덱스 계산 (-2는 테두리 보정값)
    const relativeRowIndex = Math.floor((y - HEADER_HEIGHT - 2) / ROW_HEIGHT_PIXELS);

    // 유효하지 않은 행 인덱스 검사
    if (relativeRowIndex >= rows.length) return null;
    if (relativeRowIndex < 0) return null;

    // 실제 행 인덱스와 해당 행 데이터 가져오기
    const rowIndex = startRowIndex + relativeRowIndex;
    const row = rows[rowIndex];
    // 행이 존재하지 않거나 보이는 영역을 벗어난 경우 null 반환
    if (row === undefined || relativeRowIndex >= visibleRowCount) return null;

    // 수평 스크롤을 고려한 절대 X 좌표 계산
    const absoluteX = x + tableState.scrollX;
    let accumulatedWidth = 0;
    let columnIndex = -1;

    // 클릭 위치에 해당하는 컬럼 찾기
    for (let i = 0; i < nonGroupColumns.length; i++) {
        const column = nonGroupColumns[i];
        if (!column) continue;

        accumulatedWidth += column.width;
        if (absoluteX < accumulatedWidth) {
            columnIndex = i;
            break;
        }
    }

    // 유효한 컬럼을 찾지 못한 경우 null 반환
    if (columnIndex === -1) return null;

    return { rowIndex, columnIndex };
};

/**
 * 입력된 키가 출력 가능한 문자인지 확인하는 함수
 * 
 * @param key - 검사할 키 문자열
 * @returns boolean - 출력 가능한 문자이면 true, 아니면 false
 * 
 * 정규식 패턴:
 * - \p{L}: 모든 유니코드 문자 (알파벳)
 * - \p{N}: 모든 숫자
 * - \p{P}: 모든 구두점
 * - \p{S}: 모든 기호
 */
export const isPrintableKey = (key: string) => {
    return /^\p{L}$|^\p{N}$|^\p{P}$|^\p{S}$/u.test(key);
};

/**
 * 마우스 이벤트의 캔버스 내 상대 좌표를 계산하는 함수
 * 
 * @param e - 마우스 이벤트 객체
 * @param canvasRef - 캔버스 요소에 대한 참조
 * @returns {{ x: number, y: number }} - 캔버스 내 상대 좌표
 */
export const getMousePosition = (
    e: MouseEvent,
    canvasRef: React.RefObject<HTMLCanvasElement>
): { x: number; y: number } => {
    let rect;
    // 이벤트 대상이 Element인 경우
    if (e.currentTarget instanceof Element) {
        rect = e.currentTarget.getBoundingClientRect();
    }
    // 캔버스 참조가 있는 경우
    else if (canvasRef.current) {
        rect = canvasRef.current.getBoundingClientRect();
    }
    // 둘 다 없는 경우 기본값 사용
    else {
        rect = { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
    }

    // 전역 좌표에서 캔버스의 위치를 빼서 상대 좌표 계산
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    return { x, y };
};



/**
 * 수직 스크롤바의 크기와 위치를 계산하는 함수
 * 
 * @param tableId - 테이블의 고유 식별자
 * @param canvasRef - Canvas 요소에 대한 참조
 * @returns {{
 *   scrollbarHeight: number, // 스크롤바의 전체 높이
 *   handleHeight: number,    // 스크롤바 핸들의 높이
 *   handleY: number         // 스크롤바 핸들의 Y 좌표 위치
 * }}
 * 
 * @description
 * 1. 테이블의 전체 콘텐츠 높이와 보이는 영역의 높이를 비교하여 스크롤바 크기를 계산
 * 2. 스크롤바 핸들의 크기는 보이는 영역과 전체 콘텐츠의 비율에 따라 결정
 * 3. 핸들의 최소 크기는 MIN_SCROLLBAR_HANDLE_SIZE로 제한
 * 4. 현재 스크롤 위치에 따라 핸들의 Y 좌표를 계산
 */
export const getVerticalScrollbarBounds = (
    tableId: string,
    canvasRef: React.RefObject<HTMLCanvasElement>
) => {
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    const rows = makeSelectFilteredRowsByTableId(tableId)(RootState.getState());

    const canvas = canvasRef.current;
    if (!canvas) return { scrollbarHeight: 0, handleHeight: 0, handleY: 0 };

    const canvasHeight = canvas.clientHeight;
    const headerHeight = calculateHeaderHeight(tableState.columns);
    const summaryHeight = tableState.hasSummaryColumn ?
        (calculateSummaryColumnMaxDepth(Object.values(tableState.summaryColumns)) + 1) * ROW_HEIGHT : 0;

    // 전체 컨텐츠 높이 계산 (헤더 제외, 행 데이터만)
    const totalContentHeight = rows.length * ROW_HEIGHT;

    // 스크롤바 높이 계산 - 가로 스크롤바가 있을 때만 SCROLLBAR_WIDTH를 뺌
    const horizontalScrollbarSpace = tableState.hasHorizontalScrollbar ? SCROLLBAR_WIDTH : 0;
    const scrollbarHeight = canvasHeight - headerHeight - summaryHeight - horizontalScrollbarSpace;

    // 보이는 영역 높이 (헤더와 summary 제외)
    const visibleContentHeight = canvasHeight - headerHeight - summaryHeight - horizontalScrollbarSpace;

    const visibleRatio = Math.min(1, visibleContentHeight / Math.max(totalContentHeight, visibleContentHeight));
    const handleHeight = Math.max(scrollbarHeight * visibleRatio, MIN_SCROLLBAR_HANDLE_SIZE);

    // 스크롤 핸들 위치 계산 시 headerHeight 사용
    const handleY = tableState.maxScrollY > 0
        ? (tableState.scrollY / tableState.maxScrollY) * (scrollbarHeight - handleHeight) + headerHeight
        : headerHeight;

    return {
        scrollbarHeight,
        handleHeight,
        handleY,
    };
}

/**
 * 수평 스크롤바의 크기와 위치를 계산하는 함수
 * 
 * @param tableId - 테이블의 고유 식별자
 * @param canvasRef - Canvas 요소에 대한 참조
 * @returns {{
 *   scrollbarWidth: number, // 스크롤바의 전체 너비
 *   handleWidth: number,    // 스크롤바 핸들의 너비
 *   handleX: number         // 스크롤바 핸들의 X 좌표 위치
 * }}
 * 
 * @description
 * 1. 테이블의 전체 콘텐츠 너비와 보이는 영역의 너비를 비교하여 스크롤바 크기를 계산
 * 2. 스크롤바 핸들의 크기는 보이는 영역과 전체 콘텐츠의 비율에 따라 결정
 * 3. 핸들의 최소 크기는 MIN_SCROLLBAR_HANDLE_SIZE로 제한
 * 4. 현재 스크롤 위치에 따라 핸들의 X 좌표를 계산
 */
export const getHorizontalScrollbarBounds = (
    tableId: string,
    canvasRef: React.RefObject<HTMLCanvasElement>
) => {
    // 테이블 상태와 컬럼 정보 가져오기
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    const columns = makeSelectTableColumnsByTableId(tableId)(RootState.getState());

    // Canvas가 없는 경우 기본값 반환
    const canvas = canvasRef.current;
    if (!canvas) return { scrollbarWidth: 0, handleWidth: 0, handleX: 0 };

    // Canvas의 너비와 전체 콘텐츠 너비 계산 (최상위 컬럼만 합산)
    const canvasWidth = canvas.clientWidth;
    const totalContentWidth = Object.values(columns).reduce((acc, column) => {
        if (!column.parent && column.visible !== false) {
            return acc + column.width;
        }
        return acc;
    }, 0);

    // 스크롤바 너비 계산 - 세로 스크롤바가 있고 SummaryColumn이 없을 때만 SCROLLBAR_WIDTH를 뺌
    const verticalScrollbarSpace = tableState.hasVerticalScrollbar && !tableState.hasSummaryColumn ? SCROLLBAR_WIDTH : 0;
    const scrollbarWidth = canvasWidth - verticalScrollbarSpace;

    // 보이는 영역과 전체 콘텐츠의 비율 계산
    const visibleRatio = (scrollbarWidth) / totalContentWidth;

    // 스크롤바 핸들의 너비 계산 (최소 크기 제한 적용)
    const handleWidth = Math.max(scrollbarWidth * visibleRatio, MIN_SCROLLBAR_HANDLE_SIZE);

    // 현재 스크롤 위치에 따른 핸들의 X 좌표 계산
    const handleX = tableState.maxScrollX > 0
        ? (tableState.scrollX / tableState.maxScrollX) * (scrollbarWidth - handleWidth)
        : 0;

    return {
        scrollbarWidth,
        handleWidth,
        handleX,
    };
}

/**
 * 테이블의 전체 콘텐츠 영역의 크기를 계산하는 함수
 * 
 * @param tableId - 테이블의 고유 식별자
 * @returns {{
 *   contentWidth: number, // 전체 콘텐츠의 너비 (최상위 컬럼 너비의 합)
 *   contentHeight: number // 전체 콘텐츠의 높이 (모든 행의 높이 합 + 헤더 높이)
 * }}
 * 
 * @description
 * - contentWidth는 최상위 컬럼의 너비를 합산한 값 (임계점 체크와 동일한 방식)
 * - contentHeight는 (행 개수 + 1) * 행 높이로 계산 (1은 헤더를 위한 추가 행)
 */
export const getContentBounds = (tableId: string): { contentWidth: number, contentHeight: number } => {
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    const rows = makeSelectFilteredRowsByTableId(tableId)(RootState.getState());

    if (!tableState) return { contentWidth: 0, contentHeight: 0 };

    // 최상위 컬럼만 합산 (임계점 체크와 동일한 방식)
    const contentWidth = Object.values(tableState.columns).reduce((acc, column) => {
        if (!column.parent && column.visible !== false) {
            return acc + column.width;
        }
        return acc;
    }, 0);

    // 전체 행 수에 헤더 행을 더해 높이 계산
    const contentHeight = (rows.length + 1) * ROW_HEIGHT;

    return { contentWidth, contentHeight };
};




export const formatValue = (value: any, format?: string): string => {
    // null, undefined 값 처리를 먼저 확인
    if (value === null || value === undefined) return '';

    if (!format) return String(value);

    // :가 있는 경우에만 분리
    const [formatType, formatParam] = format.split(':');
    if (value === '' || value === undefined || value === null) return '';

    switch (formatType) {
        case 'currency':
            return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'USD' }).format(value);
        case 'percent':
            return new Intl.NumberFormat('ko-KR', { style: 'percent' }).format(value);
        case 'decimal':
            return new Intl.NumberFormat('ko-KR', { style: 'decimal' }).format(value);
        case 'locale':
            return new Intl.NumberFormat('ko-KR').format(value);
        case 'date':
            return new Date(value).toLocaleDateString('ko-KR');
        case 'time':
            return new Date(value).toLocaleTimeString('ko-KR');
        case 'datetime':
            return new Date(value).toLocaleString('ko-KR');
        case 'padded':
            const padLength = parseInt(formatParam, 10);
            return String(value).padStart(padLength, '0');
        case 'maxLength': // 새로운 포맷 타입 추가
            const maxLength = parseInt(formatParam, 10);
            const strValue = String(value);
            return strValue.length > maxLength ? strValue.substring(0, maxLength) : strValue;
        default:
            return String(value);
    }
};



/**
 * 마우스 위치에 해당하는 컬럼을 찾고 리사이즈 핸들 근처인지 확인하는 함수
 * 
 * @param x - 마우스의 X 좌표
 * @param y - 마우스의 Y 좌표
 * @param columns - 컬럼 정보를 담고 있는 객체 맵
 * @param scrollX - 수평 스크롤 위치
 * @returns SelectedColumn - 선택된 컬럼 정보 ({id: string, grabbed: boolean})
 */
export const getSelectedColumn = (
    x: number,
    y: number,
    columns: { [columnIndex: number]: Column },
    scrollX: number
): SelectedColumn => {
    /**
     * 컬럼의 너비를 반환하는 헬퍼 함수
     */
    const getColumnWidth = (column: Column): number => {
        return column.width;
    };

    /**
     * 주어진 위치에 있는 컬럼을 재귀적으로 찾는 함수
     * 
     * @param x - 마우스 X 좌표
     * @param y - 마우스 Y 좌표
     * @param columnsArr - 검사할 컬럼 배열
     * @param depth - 현재 깊이 레벨 (중첩된 그룹 컬럼의 깊이)
     * @param accumulatedX - 누적된 X 좌표 (이전 컬럼들의 너비 합)
     * @param accumulatedY - 누적된 Y 좌표 (상위 컬럼들의 높이 합)
     * @returns {
     *   column: 찾은 컬럼 또는 null,
     *   grabbed: 리사이즈 핸들 근처 여부,
     *   accX: 현재까지의 누적 X 좌표
     * }
     */

    const findColumnAtPosition = (
        x: number,
        y: number,
        columnsArr: Column[],
        depth: number = 0,
        accumulatedX: number = 0,
        accumulatedY: number = 0
    ): { column: Column | null; grabbed: boolean; accX: number } => {
        for (const column of columnsArr) {
            // visible이 false인 컬럼은 건너뛰기
            if (column.visible === false) continue;

            const columnWidth = getColumnWidth(column);
            const columnStartX = accumulatedX;
            const columnEndX = columnStartX + columnWidth;
            const rowSpan = column.rowspan || 1;

            // 컬럼의 세로 범위 계산
            const startY = accumulatedY;
            const endY = startY + ROW_HEIGHT * rowSpan;

            // 마우스가 컬럼의 X축 범위 내에 있는지 확인
            const isWithinXRange = x >= columnStartX - scrollX && x <= columnEndX - scrollX;
            // 마우스가 리사이즈 핸들 근처(3px 이내)에 있는지 확인
            const isNearResizeHandle = Math.abs(x - (columnEndX - scrollX)) < 3;

            if (column.type === ValueType.GROUP && column.children) {
                // 그룹 컬럼인 경우
                if (y >= startY && y < endY) {

                    if (isNearResizeHandle) {
                        return { column, grabbed: true, accX: columnEndX };
                    }
                    if (isWithinXRange) {
                        return { column, grabbed: false, accX: columnEndX };
                    }
                }

                if (isWithinXRange) {
                    const childResult = findColumnAtPosition(
                        x,
                        y,
                        column.children,
                        depth + 1,
                        columnStartX,
                        startY + (rowSpan * ROW_HEIGHT)
                    );

                    if (childResult.column) {
                        return childResult;
                    }
                }
            } else {
                // 일반 컬럼인 경우
                if (y >= startY && y < endY) {
                    if (isNearResizeHandle) {
                        return { column, grabbed: true, accX: columnEndX };
                    }
                    if (isWithinXRange) {
                        return { column, grabbed: false, accX: columnEndX };
                    }
                }
            }

            accumulatedX = columnEndX;
        }
        return { column: null, grabbed: false, accX: accumulatedX };
    };

    // 최상위 컬럼들에 대해 검사 수행
    const { column, grabbed } = findColumnAtPosition(x, y, Object.values(columns));

    return {
        id: column?.id || '',
        grabbed
    };
};



/**
 * 정렬 타입을 순환하는 함수
 * 
 * @param currentOrderType - 현재 정렬 타입 ('asc', 'desc', undefined)
 * @returns 다음 정렬 타입
 * 
 * @description
 * 정렬 타입은 다음 순서로 순환됩니다:
 * undefined -> 'desc' -> 'asc' -> undefined
 * 
 * @example
 * toggleOrderType(undefined) // returns 'desc'
 * toggleOrderType('desc')   // returns 'asc'
 * toggleOrderType('asc')    // returns undefined
 */
export const toggleOrderType = (currentOrderType?: string) => {
    switch (currentOrderType) {
        case undefined:
            return 'desc';
        case 'desc':
            return 'asc';
        default:
            return undefined;
    }
}


/**
 * 현재 셀 위치에서 다음 셀의 위치를 계산하는 함수
 * 
 * @param currentRowIndex - 현재 행 인덱스
 * @param currentColumnIndex - 현재 열 인덱스
 * @param totalColumns - 전체 열 개수
 * @param totalRows - 전체 행 개수
 * @param autoAddRow - 마지막 행에서 자동으로 새 행 추가 여부
 * @returns {{
 *   nextRowIndex: number,
 *   nextColumnIndex: number,
 *   shouldAddNewRow: boolean
 * }} 다음 셀 위치 정보
 * 
 * @description
 * 1. 현재 열에서 다음 열로 이동
 * 2. 마지막 열인 경우 다음 행의 첫 번째 열로 이동
 * 3. autoAddRow가 true이고 마지막 행인 경우 새 행 추가 플래그 설정
 * 
 * @example
 * const result = getNextCellPosition(0, 2, 4, 3, true);
 * // returns { nextRowIndex: 0, nextColumnIndex: 3, shouldAddNewRow: false }
 */
export const getNextCellPosition = (
    currentRowIndex: number,
    currentColumnIndex: number,
    totalColumns: number,
    totalRows: number,
    autoAddRow: boolean,
    columns?: any[]
): { nextRowIndex: number, nextColumnIndex: number, shouldAddNewRow: boolean } => {
    let nextColumnIndex = currentColumnIndex;
    let nextRowIndex = currentRowIndex;
    let shouldAddNewRow = false;

    // 다음 컬럼으로 이동
    if (nextColumnIndex + 1 < totalColumns) {
        nextColumnIndex += 1;

        // checkbox 타입이면 건너뛰기
        if (columns && columns[nextColumnIndex]?.type === ValueType.CHECKBOX) {
            // 재귀 호출로 다음 non-checkbox 컬럼 찾기
            return getNextCellPosition(currentRowIndex, nextColumnIndex, totalColumns, totalRows, autoAddRow, columns);
        }
    } else {
        // 다음 행의 첫 번째 컬럼으로 이동
        nextColumnIndex = 0;
        nextRowIndex += 1;

        // 첫 번째 컬럼이 checkbox 타입이면 건너뛰기
        if (columns && columns[nextColumnIndex]?.type === ValueType.CHECKBOX) {
            return getNextCellPosition(nextRowIndex, nextColumnIndex, totalColumns, totalRows, autoAddRow, columns);
        }

        if (autoAddRow && nextRowIndex >= totalRows) {
            shouldAddNewRow = true;
        }
    }

    return { nextRowIndex, nextColumnIndex, shouldAddNewRow };
};


export const getColumnIndex = (columnId: string, columns: { [columnId: string]: BaseColumn }): number => {
    return Object.values(columns).findIndex(col => col.id === columnId);
};



/**
 * 컬럼의 전체 너비를 계산하는 함수
 * 
 * @param column - 너비를 계산할 컬럼 객체
 * @param columns - 전체 컬럼 맵 (key: columnId, value: Column)
 * @returns 계산된 전체 너비
 * 
 * 동작 방식:
 * 1. GROUP 타입 컬럼인 경우:
 *    - 모든 자식 컬럼의 너비를 재귀적으로 합산
 * 2. 일반 컬럼인 경우:
 *    - colspan 값에 따라 연속된 컬럼들의 너비를 합산
 *    - colspan이 없으면 기본값 1 사용
 */
export const calculateColumnWidth = (column: BaseColumn, columns: { [columnId: string]: BaseColumn }): number => {
    // GROUP 타입이고 자식 컬럼이 있는 경우
    if (column.type === ValueType.GROUP && column.children) {
        // 모든 자식 컬럼의 너비를 재귀적으로 합산하여 반환
        return column.children.reduce((sum, child) => {
            return sum + calculateColumnWidth(child, columns);
        }, 0);
    }

    // 일반 컬럼의 경우
    const colspan = column.colspan || 1;  // colspan이 없으면 기본값 1
    // 현재 컬럼부터 colspan 만큼의 컬럼 너비를 합산
    return Object.values(columns)
        .slice(getColumnIndex(column.id, columns), getColumnIndex(column.id, columns) + colspan)
        .reduce((sum, col) => sum + (col.width || 0), 0);
};




/**
 * 계층형 컬럼 구조에서 전체 헤더 높이를 계산하는 함수
 * 
 * @param columns - 컬럼 정보를 담고 있는 객체
 *                 key: columnId, value: Column 객체
 * @returns number - 전체 헤더 높이 (픽셀 단위)
 * 
 * 동작 방식:
 * 1. 최상위 컬럼(parent가 없는)부터 시작하여 재귀적으로 자식 컬럼을 탐색
 * 2. 가장 깊은 계층의 레벨을 찾아 maxLevel에 저장
 * 3. (maxLevel + 1) * ROW_HEIGHT로 전체 높이 계산
 * 
 * 예시:
 * Column A (level 0)
 *   ├─ Child A1 (level 1)
 *   └─ Child A2 (level 1)
 *       └─ Child A2-1 (level 2)
 * Column B (level 0)
 * 
 * maxLevel = 2이므로, 높이 = (2 + 1) * ROW_HEIGHT
 */
export const calculateHeaderHeight = (columns: { [columnId: string]: Column }): number => {
    let maxLevel = 0;
    const processColumn = (column: Column, level: number = 0) => {
        maxLevel = Math.max(maxLevel, level);
        column.children?.forEach(child => processColumn(child, level + 1));
    };

    Object.values(columns)
        .filter(column => !column.parent)
        .forEach(column => processColumn(column));
    return (maxLevel + 1) * ROW_HEIGHT;
};




/**
 * 선명한 픽셀 렌더링을 위한 좌표 보정 함수
 * @param pixel - 보정할 픽셀 좌표 값
 * @param thickness - 선의 두께 (기본값: 1)
 * @returns 보정된 픽셀 좌표 값
 */
export const crispPixel = (pixel: number, thickness: number = 1): number => {
    // 디바이스 픽셀 비율 가져오기
    const dpr = window.devicePixelRatio || 1;

    // 실제 물리적 픽셀 좌표로 변환
    const physicalPixel = pixel * dpr;

    let alignedPixel: number;

    if (thickness % 2 === 0) {
        // 짝수 두께: 정수 픽셀에 정렬
        alignedPixel = Math.round(physicalPixel);
    } else {
        // 홀수 두께: 픽셀 경계에 정렬 (정수 + 0.5)
        alignedPixel = Math.floor(physicalPixel) + 0.5;
    }

    // 논리적 픽셀 좌표로 다시 변환
    return alignedPixel / dpr;
};


export const calculateSummaryColumnMaxDepth = (summaryColumns: SummaryColumn[]): number => {
    return Math.max(...summaryColumns.map(col =>
        col.children ? 1 + calculateSummaryColumnMaxDepth(col.children) : 0
    ));
};


/**
 * 셀의 읽기 전용 상태를 확인하는 함수
 * 
 * @param tableId - 테이블 ID
 * @param rowId - 행 ID
 * @param columnId - 열 ID
 * @returns boolean - 읽기 전용 여부
 * 
 * @description
 * 우선순위: 셀 > 행 > 열 순서로 readOnly 속성을 확인
 * 1. 데이터가 없는 경우 읽기 전용으로 처리
 * 2. 셀 레벨의 readOnly 속성 확인
 * 3. 행 레벨의 readOnly 속성 확인
 * 4. 열 레벨의 readOnly 속성 확인
 * 5. 모든 레벨에서 readOnly가 명시되지 않은 경우 false 반환
 */
export const isReadOnly = (
    tableId: string,
    rowId: string,
    columnId: string
): boolean => {
    const row = makeSelectRowById(tableId, rowId)(RootState.getState());
    const column = selectFlattenedColumns(RootState.getState(), tableId).find(col => col.id === columnId);
    const cell = row?.data[columnId];

    if (!row || !column || !cell) return true;

    // 1. 셀 레벨 readOnly 체크
    if (cell.readOnly === undefined || cell.readOnly === false) {
        // 2. 행 레벨 readOnly 체크
        if (row.readOnly === undefined || row.readOnly === false) {
            // 3. 열 레벨 readOnly 체크
            if (column.readOnly === undefined || column.readOnly === false) {
                return false;
            }
            return true;
        }
        return true;
    }
    return true;
};



/**
 * 특정 셀이 포커싱 가능한지 확인하는 함수
 * dependsOnColumn이 설정된 컬럼의 경우, 해당 참조 컬럼에 값이 있어야만 포커싱 가능
 * 
 * @param tableId - 테이블의 고유 식별자
 * @param rowId - 행의 고유 식별자
 * @param columnId - 컬럼의 고유 식별자
 * @returns boolean - 포커싱 가능 여부
 */
export const canCellBeFocused = (
    tableId: string,
    rowId: string,
    columnId: string
): boolean => {
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    const row = makeSelectRowById(tableId, rowId)(RootState.getState());
    const column = tableState.columns[columnId];

    if (!row || !column) {
        return false;
    }

    // dependsOnColumn이 설정되어 있다면 해당 컬럼에 값이 있는지 확인
    if (column.dependsOnColumn) {
        const dependsOnCell = row.data[column.dependsOnColumn];

        // 참조 컬럼에 값이 없거나 빈 값인 경우 포커싱 불가
        if (!dependsOnCell ||
            dependsOnCell.value === undefined ||
            dependsOnCell.value === null ||
            dependsOnCell.value === '') {
            return false;
        }
    }

    return true;
};



/**
 * 테이블의 세로 스크롤바 정보를 계산하는 함수
 */
export const getVerticalScrollbarInfo = (table: TableState): {
    hasVerticalScrollbar: boolean;
    availableWidth: number;
    tempVisibleHeight: number;
} => {
    const headerHeight = calculateHeaderHeight(table.columns);
    const summaryHeight = table.hasSummaryColumn ?
        (calculateSummaryColumnMaxDepth(Object.values(table.summaryColumns)) + 1) * ROW_HEIGHT : 0;
    const tempVisibleHeight = table.canvasHeight - headerHeight - summaryHeight - 1;

    // 초기화 시점에서 contentHeight가 정확하지 않을 수 있으므로 안전장치 추가
    const hasVerticalScrollbar = table.contentHeight > 0 ?
        table.contentHeight > tempVisibleHeight :
        false; // 초기화 시점에서는 스크롤바 없다고 가정

    // 세로 스크롤바 여부에 따라 사용 가능한 너비 계산
    const availableWidth = hasVerticalScrollbar ? table.canvasWidth - SCROLLBAR_WIDTH : table.canvasWidth;

    return {
        hasVerticalScrollbar,
        availableWidth,
        tempVisibleHeight
    };
};


/**
 * 스크롤 값들을 계산하는 함수
 */
export const calculateScrollValues = (table: TableState, skipAutoFit = false): void => {
    const { canvasWidth, canvasHeight, contentWidth, contentHeight, hasSummaryColumn } = table;

    // 기본 값 체크로 불필요한 계산 방지
    if (canvasWidth <= 0 || canvasHeight <= 0) {
        return;
    }

    const scrollbarInfo = getVerticalScrollbarInfo(table);
    const { hasVerticalScrollbar, availableWidth } = scrollbarInfo;

    // autoFit이 실제로 적용 가능할 때만 가로스크롤바 숨김
    const isAutoFitWorking = isAutoFitApplicable(table);
    const hasHorizontalScrollbar = isAutoFitWorking ? false : contentWidth > availableWidth;

    // 최종 사용 가능한 높이 계산 (가로 스크롤바 공간 고려)
    const headerHeight = calculateHeaderHeight(table.columns);
    const summaryHeight = hasSummaryColumn ?
        (calculateSummaryColumnMaxDepth(Object.values(table.summaryColumns)) + 1) * ROW_HEIGHT : 0;
    const finalVisibleHeight = canvasHeight - headerHeight - summaryHeight - (hasHorizontalScrollbar ? SCROLLBAR_WIDTH : 0) - 1;
    const diff = finalVisibleHeight % ROW_HEIGHT;

    const maxScrollX = Math.max(contentWidth - availableWidth, 0);
    const maxScrollY = Math.max(contentHeight - finalVisibleHeight + ROW_HEIGHT + diff, 0);

    // 실제 변경이 있는 경우에만 업데이트 (성능 최적화)
    const threshold = 0.1;
    const shouldUpdateMaxScrollX = Math.abs(table.maxScrollX - maxScrollX) > threshold;
    const shouldUpdateMaxScrollY = Math.abs(table.maxScrollY - maxScrollY) > threshold;
    const shouldUpdateScrollbars = table.hasVerticalScrollbar !== hasVerticalScrollbar ||
        table.hasHorizontalScrollbar !== hasHorizontalScrollbar;

    if (shouldUpdateMaxScrollX || shouldUpdateMaxScrollY || shouldUpdateScrollbars) {
        table.maxScrollX = maxScrollX;
        table.maxScrollY = maxScrollY;
        table.hasVerticalScrollbar = hasVerticalScrollbar;
        table.hasHorizontalScrollbar = hasHorizontalScrollbar;

        // 스크롤 위치가 최대치를 넘지 않도록 조정
        table.scrollX = Math.min(table.scrollX, maxScrollX);
        table.scrollY = Math.min(table.scrollY, maxScrollY);
    }
};


/**
 * 컬럼 ID로 컬럼을 찾는 헬퍼 함수
 */
export const findColumnById = (table: TableState, targetId: string): Column | undefined => {
    // 모든 최상위 컬럼들을 배열로 변환
    const columns = Object.values(table.columns);

    // 재귀적으로 컬럼을 검색하는 내부 함수
    function searchInColumns(columns: Column[]): Column | undefined {
        for (const column of columns) {
            // 현재 컬럼의 ID가 일치하는지 확인
            if (column.id === targetId) {
                return column;
            }

            // 자식 컬럼이 있다면 재귀적으로 검색
            if (column.children && column.children.length > 0) {
                const foundInChildren = searchInColumns(column.children);
                if (foundInChildren) {
                    return foundInChildren;
                }
            }
        }
        return undefined;
    }

    return searchInColumns(columns);
};

/**
 * autoFit이 실제로 적용 가능한지 체크하는 함수
 */
export const isAutoFitApplicable = (table: TableState): boolean => {
    if (!table.autoFit || table.canvasWidth <= 0) {
        return false;
    }

    const result = !checkAutoFitThreshold(table);
    return result;
};

/**
 * autoFit 임계점을 체크하는 함수 (컬럼 총 너비 기준)
 */
export const checkAutoFitThreshold = (table: TableState): boolean => {
    if (!table.autoFit || table.canvasWidth <= 0) {
        return false;
    }

    // 현재 컬럼 총 너비 계산
    const topLevelColumns = Object.values(table.columns).filter(col => col.visible !== false);

    if (topLevelColumns.length === 0) {
        return false;
    }

    const totalColumnWidth = topLevelColumns.reduce((sum, col) => sum + (col.ratio || 0), 0);

    // 고정된 너비 사용 (순환 참조 방지)
    const availableCanvasWidth = table.canvasWidth - SCROLLBAR_WIDTH;

    // 컬럼 총 너비가 캔버스 너비의 몇 배인지 계산
    const widthRatio = totalColumnWidth / availableCanvasWidth;

    // 임계점: 컬럼 총 너비가 사용 가능한 너비의 110%를 넘으면 autoFit 해제
    const isThresholdExceeded = widthRatio > THRESHOLD;

    return isThresholdExceeded;
};


/**
 * 모든 컬럼의 ratio를 계산하는 함수
 */
export const calculateAllColumnRatios = (columns: { [columnId: string]: Column }) => {
    // 1. 최상위 컬럼들의 ratio 계산 (정규화 없이 원본 width 사용)
    const topLevelColumns = Object.values(columns).filter(col => !col.parent && col.visible !== false);
    if (topLevelColumns.length > 0) {
        topLevelColumns.forEach(col => {
            col.ratio = col.width; // 정규화 없이 원본 width 사용
        });
    }

    // 2. GROUP 컬럼들의 자식 ratio 계산 (부모 대비 상대적인 비율)
    function calculateChildRatios(column: Column) {
        if (column.type === ValueType.GROUP && column.children?.length) {
            const parentWidth = column.width;
            if (parentWidth > 0) {
                column.children.forEach(child => {
                    // 부모 컬럼의 너비 대비 상대적인 비율로 계산
                    child.ratio = child.width / parentWidth;
                    // 재귀적으로 자식의 자식들도 처리
                    calculateChildRatios(child);
                });
            }
        }
    }

    Object.values(columns).forEach(calculateChildRatios);
};



/**
 * 컬럼의 최소 너비를 계산하는 함수
 */
export const calculateMinWidth = (column: Column): number => {
    const MIN_COLUMN_WIDTH = 30.0;

    if (column.type !== ValueType.GROUP || !column.children || column.children.length === 0) {
        return MIN_COLUMN_WIDTH;
    }

    // GROUP 컬럼의 경우 모든 자식 컬럼의 최소 너비 합계를 반환
    return column.children.reduce((sum, child) => sum + calculateMinWidth(child), 0);
};



/**
 * GROUP이 아닌 모든 컬럼들을 찾는 헬퍼 함수
 */
export const findNonGroupColumns = (table: TableState): Column[] => {
    const result: Column[] = [];

    function searchNonGroupColumns(columns: Column[]) {
        columns.forEach(column => {
            if (column.type !== ValueType.GROUP) {
                result.push(column);
            }

            // 자식 컬럼이 있다면 재귀적으로 검색
            if (column.children && column.children.length > 0) {
                searchNonGroupColumns(column.children);
            }
        });
    }

    searchNonGroupColumns(Object.values(table.columns));
    return result;
};


/**
 * 전체 컬럼 트리의 최대 깊이 계산
 */
export const getMaxDepth = (columns: Column[]): number => {
    let maxDepth = 1;
    for (const column of columns) {
        if (column.children && column.children.length > 0) {
            const childDepth = getMaxDepth(column.children) + 1;
            maxDepth = Math.max(maxDepth, childDepth);
        }
    }
    return maxDepth;
};

/**
 * 셀 선택과 스크롤 조정을 함께 처리하는 공통 함수
 */
export const selectCellAndAdjustScroll = (
    tableId: string,
    rowIndex: number,
    columnIndex: number,
    canvasRef: React.RefObject<HTMLCanvasElement>,
    dispatch: any
) => {

    // 셀 선택
    dispatch(setSelectedCell({
        tableId,
        rowIndex,
        columnIndex
    }));

    // 스크롤 조정
    const newScroll = adjustScroll(rowIndex, columnIndex, tableId, canvasRef);
    dispatch(setScroll({
        tableId,
        scrollX: newScroll.newScrollX,
        scrollY: newScroll.newScrollY
    }));
};

/**
 * ReadOnly 상태를 종합적으로 체크하는 공통 함수
 */
export const isReadOnlyCell = (
    tableId: string,
    rowId: string,
    columnId: string,
    globalReadOnly: boolean = false
): boolean => {
    if (globalReadOnly) return true;
    return isReadOnly(tableId, rowId, columnId);
};

/**
 * 셀 유효성을 종합적으로 검사하는 공통 함수
 */
export const isCellValid = (
    rowIndex: number,
    columnIndex: number,
    rows: any[],
    columns: any[]
): boolean => {
    return !(
        rowIndex === -1 ||
        columnIndex === -1 ||
        rowIndex >= rows.length ||
        columnIndex >= columns.length
    );
};

/**
 * 체크박스 일괄 업데이트를 처리하는 공통 함수 (최적화된 버전)
 */
export const handleBatchCheckboxUpdate = (
    tableId: string,
    columnId: string,
    rows: any[],
    newValue: boolean,
    dispatch: any
) => {

    // readOnly가 아니고 새로운 행이 아닌 행들만 필터링하고 한 번에 업데이트할 데이터 준비
    const rowsToUpdate = rows
        .filter(row => !row.data.isNewRow?.value && !row.data[columnId]?.readOnly)
        .map(row => ({
            id: row.id,
            data: {
                ...row.data,
                [columnId]: {
                    ...row.data[columnId],
                    value: newValue
                }
            }
        }));

    // 한 번의 dispatch로 모든 행 업데이트
    if (rowsToUpdate.length > 0) {
        dispatch(updateRows({
            tableId,
            rows: rowsToUpdate
        }));
    }

    // 체크된 행 데이터 관리 - 한 번의 dispatch로 처리
    if (newValue) {
        // 체크 상태로 변경된 행들을 한 번에 추가 (새로운 행이 아니고 readOnly가 아닌 행만)
        const rowsToAdd = rows.filter(row => !row.data.isNewRow?.value && !row.data[columnId]?.readOnly);
        if (rowsToAdd.length > 0) {
            dispatch(addCheckedRowData(rowsToAdd));
        }
    } else {
        // 체크 해제된 행들을 한 번에 제거
        const idsToRemove = rowsToUpdate.map(row => row.id);
        if (idsToRemove.length > 0) {
            dispatch(removeCheckedRowData(idsToRemove));
        }
    }
};

/**
 * 모든 체크박스 상태를 확인하는 공통 함수
 */
export const areAllCheckboxesChecked = (
    rows: any[],
    columnId: string
): boolean => {
    return rows.every(row =>
        row.data.isNewRow?.value || row.data[columnId]?.readOnly ? true : row.data[columnId]?.value === true
    );
};